import { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '../api';
import { DateRangeFilter } from '../components/Filters';
import { ColFilter } from '../components/ColFilter';

function ProgressBar({ label, value, max, sub }) {
  const percent = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="progress-item" style={{ marginBottom: '1.2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '13px' }}>
        <span style={{ fontWeight: 600, color: '#2C3E50' }}>{label}</span>
        <span style={{ fontWeight: 600, color: '#34495E' }}>{value.toLocaleString('en-IN')} / {max.toLocaleString('en-IN')} ({percent}%)</span>
      </div>
      <div style={{ background: '#E0E7ED', borderRadius: '6px', height: '8px', overflow: 'hidden' }}>
        <div style={{ background: '#2E86AB', height: '100%', width: `${percent}%` }} />
      </div>
      {sub && <div style={{ fontSize: '11px', color: '#7F8C8D', marginTop: '4px' }}>{sub}</div>}
    </div>
  );
}

export default function Icegate() {
    // Redirect to login page if DevTools or View Source shortcuts are pressed
    useEffect(() => {
      const handleKeyDown = (e) => {
        // F12
        if (e.key === 'F12') {
          window.location.href = '/login';
          e.preventDefault();
        }
        // Ctrl+Shift+I or Cmd+Opt+I
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'i')) {
          window.location.href = '/login';
          e.preventDefault();
        }
        // Ctrl+U or Cmd+U
        if ((e.ctrlKey || e.metaKey) && (e.key === 'U' || e.key === 'u')) {
          window.location.href = '/login';
          e.preventDefault();
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  // Controls UI state
  const [activeTab, setActiveTab] = useState('iec'); 
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  // New Filters
  const [dateFilters, setDateFilters] = useState({ fromDate: '', toDate: '' });
  const [contactInfo, setContactInfo] = useState(''); // email, phone, both, either
  const [dbkStatusFilter, setDbkStatusFilter] = useState(''); // Sanctioned vs Unsanctioned dropdown
  // Table Column Filters
  const [iecStatus, setIecStatus] = useState('');
  const [sbLocation, setSbLocation] = useState('');
  const [dbkStatusCol, setDbkStatusCol] = useState('');
  const [dgftError, setDgftError] = useState('');
  // City/State filter state for IEC tab
  const [cityFilter, setCityFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const limit = 50;

  const buildParams = useCallback(() => {
    const p = { page, limit, type: activeTab };
    if (search) p.search = search;
    // Dates apply to SB, DBK, and DGFT
    if (activeTab !== 'iec') {
      if (dateFilters.fromDate) p.fromDate = dateFilters.fromDate;
      if (dateFilters.toDate) p.toDate = dateFilters.toDate;
    }
    if (activeTab === 'iec') {
      if (contactInfo) p.contactInfo = contactInfo;
      if (iecStatus) p.iecStatus = iecStatus;
      if (cityFilter) p.city = cityFilter;
      if (stateFilter) p.state = stateFilter;
    }
    if (activeTab === 'sb') {
      if (sbLocation) p.sbLocation = sbLocation;
    }
    if (activeTab === 'dbk') {
      if (dbkStatusFilter) p.statusFilter = dbkStatusFilter;
      if (dbkStatusCol) p.dbkStatus = dbkStatusCol;
    }
    if (activeTab === 'dgft') {
      if (dgftError) p.dgftError = dgftError;
    }
    return p;
  }, [page, search, activeTab, dateFilters, contactInfo, iecStatus, sbLocation, dbkStatusFilter, dbkStatusCol, dgftError, cityFilter, stateFilter]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.icegate.summary(),
        api.icegate.records(buildParams())
      ]);
      setSummary(s);
      setRecords(r.rows);
      setTotal(r.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [buildParams]);

  useEffect(() => { loadData(); }, [loadData]);
  
  // Reset filters entirely when switching tabs
  useEffect(() => { 
    setPage(1); 
    setSearch(''); 
    setDateFilters({ fromDate: '', toDate: '' });
    setContactInfo('');
    setDbkStatusFilter('');
    setIecStatus(''); setSbLocation(''); setDbkStatusCol(''); setDgftError('');
  }, [activeTab]);

  // Reset page when column filters change
  useEffect(() => { setPage(1); }, [iecStatus, sbLocation, dbkStatusCol, dgftError, contactInfo, cityFilter, stateFilter]);

  // ─── DYNAMIC CASCADING FILTERS ───
  // Automatically shrink the dropdown options so users never select impossible combinations
  const availableDbkStatuses = useMemo(() => {
    if (!summary?.dbkStatuses) return [];
    if (dbkStatusFilter === 'Sanctioned') return ['Sanctioned']; // Only show Sanctioned
    if (dbkStatusFilter === 'Unsanctioned') return summary.dbkStatuses.filter(s => s !== 'Sanctioned'); // Hide Sanctioned
    return summary.dbkStatuses; // Show all if no page filter applied
  }, [summary?.dbkStatuses, dbkStatusFilter]);

  const fmt = n => n == null ? '—' : Number(n).toLocaleString('en-IN');
  const formatStatus = (val) => val ? String(val).replace(/\.0$/, '') : '—';
  const hasColFilters = iecStatus || sbLocation || dbkStatusCol || dgftError;

  // Disable right-click context menu
  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  return (
    <div className="page" onContextMenu={handleContextMenu}>
      <div className="page-header">
        <h1 className="page-title">Icegate Intelligence</h1>
        <p className="page-desc">Database coverage analysis between Shipping Bills, Drawbacks, and DGFT</p>
      </div>

      <div className="stat-grid" style={{ marginBottom: '1.5rem' }}>
        <div className={`stat-card stat-card--clickable ${activeTab === 'iec' ? 'stat-card--accent' : ''}`} onClick={() => setActiveTab('iec')}>
          <div className="stat-value">{fmt(summary?.TOTAL_IEC)}</div>
          <div className="stat-label">Master IEC Companies</div>
          <div className="stat-cta">View Table →</div>
        </div>
        <div className={`stat-card stat-card--clickable ${activeTab === 'sb' ? 'stat-card--accent' : ''}`} onClick={() => setActiveTab('sb')}>
          <div className="stat-value">{fmt(summary?.TOTAL_SB)}</div>
          <div className="stat-label">Shipping Bills (Master)</div>
          <div className="stat-cta">View Table →</div>
        </div>
        <div className={`stat-card stat-card--clickable ${activeTab === 'dbk' ? 'stat-card--accent' : ''}`} onClick={() => setActiveTab('dbk')}>
          <div className="stat-value" style={{ color: '#44BBA4' }}>{fmt(summary?.TOTAL_DBK)}</div>
          <div className="stat-label">Drawback Records</div>
          <div className="stat-cta">View Table →</div>
        </div>
        <div className={`stat-card stat-card--clickable ${activeTab === 'dgft' ? 'stat-card--accent' : ''}`} onClick={() => setActiveTab('dgft')}>
          <div className="stat-value" style={{ color: '#E8C547' }}>{fmt(summary?.TOTAL_DGFT)}</div>
          <div className="stat-label">Total DGFT Records</div>
          <div className="stat-cta">View Table →</div>
        </div>
      </div>

      <div className="chart-card" style={{ marginBottom: '2rem', padding: '20px' }}>
        <h3 style={{ fontSize: '14px', marginBottom: '16px', color: '#0B2C4A' }}>Database Coverage Analysis</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
          <ProgressBar label="Sanctioned DBKs in SB Master" value={summary?.DBK_SANC_WITH_SB || 0} max={summary?.DBK_SANCTIONED || 0} sub="Sanctioned Drawbacks synced correctly" />
          <ProgressBar label="Unsanctioned / Pending DBKs" value={summary?.DBK_UNSANCTIONED || 0} max={summary?.TOTAL_DBK || 0} sub="Queries, Suspensions, or Pending Scrolls" />
          <ProgressBar label="DGFT Records Missing from SB" value={summary?.DGFT_MISSING_SB || 0} max={summary?.TOTAL_DGFT || 0} sub="DGFT records not matched to Shipping Bills" />
        </div>
      </div>

      {/* --- Filters --- */}
      <div className="filter-section">
        <div className="filter-row">
          <input 
            className="search-input search-input--lg"
            placeholder={`Search ${activeTab.toUpperCase()} records by ID or Name...`}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          
          {activeTab !== 'iec' && (
            <DateRangeFilter filters={dateFilters} onChange={f => { setDateFilters(f); setPage(1); }} label="SHIPPING BILL DATE" />
          )}

          {activeTab === 'iec' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '12px' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 600 }}>CONTACT INFO:</span>
              <select value={contactInfo} onChange={e => { setContactInfo(e.target.value); setPage(1); }} className="form-select" style={{ padding: '0.4rem', border: '1px solid var(--border)', borderRadius: '6px' }}>
                <option value="">All Types</option>
                <option value="email">Has Email</option>
                <option value="phone">Has Phone</option>
                <option value="both">Both Email & Phone</option>
                <option value="either">Either Found</option>
              </select>
            </div>
          )}

          {activeTab === 'dbk' && (
            <select className="form-select" style={{ padding: '0.4rem', border: '1px solid var(--border)', borderRadius: '6px' }} value={dbkStatusFilter} onChange={e => { setDbkStatusFilter(e.target.value); setDbkStatusCol(''); setPage(1); }}>
              <option value="">All DBK Categories</option>
              <option value="Sanctioned">Sanctioned Only</option>
              <option value="Unsanctioned">Unsanctioned / Pending</option>
            </select>
          )}
        </div>
      </div>

      {/* --- Dynamic Tables --- */}
      <div className="table-card">
        {loading ? <div className="table-loading"><div className="spinner"/></div> : (
          <>
            <div className="table-meta-bar">
              <span>
                {hasColFilters 
                  ? <>Showing <strong>{records?.length || records.length}</strong> of {fmt(total)} records (Column Filters Active)</>
                  : <>Showing <strong>{fmt(total)}</strong> {activeTab.toUpperCase()} records</>}
              </span>
              {hasColFilters && (
                <button className="col-filter-reset" onClick={() => { setIecStatus(''); setSbLocation(''); setDbkStatusCol(''); setDgftError(''); }}>
                  Reset column filters
                </button>
              )}
            </div>
            
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  {activeTab === 'iec' && <tr>
                    <th>IEC Code</th><th>Company Name</th><th>PAN</th>
                    <th><ColFilter label="City" options={[...new Set(records.map(r => r.CITY).filter(Boolean))].sort().map(v => ({ value: v, label: v }))} value={cityFilter} onChange={setCityFilter} width={140} /></th>
                    <th><ColFilter label="State" options={[...new Set(records.map(r => r.STATE_ADDR).filter(Boolean))].sort().map(v => ({ value: v, label: v }))} value={stateFilter} onChange={setStateFilter} width={140} /></th>
                    <th><ColFilter label="Status" options={summary?.iecStatuses} value={iecStatus} onChange={setIecStatus} width={160} /></th>
                    <th>Mobile</th><th>Email</th>
                  </tr>}
                  
                  {activeTab === 'sb' && <tr>
                    <th>SB No</th><th>SB Date</th>
                    <th><ColFilter label="Location" options={summary?.sbLocations} value={sbLocation} onChange={setSbLocation} width={140} /></th>
                    <th>IEC</th><th>FOB (INR)</th><th>Drawback Amount</th>
                  </tr>}
                  
                  {activeTab === 'dbk' && <tr>
                    <th>SB No</th><th>SB Date</th><th>Company Name</th><th>IEC</th><th>DBK Amt</th><th>Scroll No</th>
                    <th><ColFilter label="Status" options={availableDbkStatuses} value={dbkStatusCol} onChange={setDbkStatusCol} width={220} /></th>
                  </tr>}
                  
                  {activeTab === 'dgft' && <tr>
                    <th>SB No</th><th>SB Date</th><th>IEC</th>
                    <th><ColFilter label="Error Code" options={summary?.dgftErrors} value={dgftError} onChange={setDgftError} width={180} /></th>
                    <th>Customs File</th>
                  </tr>}
                </thead>
                <tbody>
                  {records.map((r, i) => (
                    <tr key={i}>
                      {activeTab === 'iec' && <>
                        <td className="mono">{r.IEC_CODE}</td><td className="td-company">{r.COMPANY_NAME}</td><td className="mono">{r.PAN_NUMBER}</td>
                        <td>{r.CITY || '—'}</td><td>{r.STATE_ADDR || '—'}</td>
                        <td><span className="status-badge" style={{ background:'#eee', color:'#333'}}>{formatStatus(r.IEC_STATUS)}</span></td>
                        <td>{r.MOBILE_NO ? <span className="contact-value">{String(r.MOBILE_NO).replace(/\.0$/, '')}</span> : <span className="no-data">—</span>}</td>
                        <td className="small-text">{r.EMAIL_ID ? <span className="contact-value">{r.EMAIL_ID}</span> : <span className="no-data">—</span>}</td>
                      </>}
                      
                      {activeTab === 'sb' && <>
                        <td className="mono">{r.SB_NO}</td><td className="date-cell">{r.SB_DATE_FMT || '—'}</td>
                        <td>{r.LOCATION_CODE}</td><td className="mono">{r.IEC_CODE}</td>
                        <td>{fmt(r.FOB_INR)}</td><td style={{ color: '#44BBA4', fontWeight: 600 }}>{fmt(r.DRAWBACK_AMOUNT)}</td>
                      </>}

                      {activeTab === 'dbk' && <>
                        <td className="mono">{r.SHIPPING_BILL_NO}</td><td className="date-cell">{r.SB_DATE_FMT || '—'}</td>
                        <td className="td-company">{r.COMPANY_NAME || '—'}</td><td className="mono">{r.IEC_CODE}</td>
                        <td style={{ color: '#44BBA4', fontWeight: 600 }}>{fmt(r.DBK_AMOUNT)}</td>
                        <td>{r.SCROLL_NO || '—'}</td>
                        <td><span className={r.STATUS_TYPE === 'Sanctioned' ? 'badge-yes' : 'badge-no'}>{r.STATUS_TYPE || 'Pending'}</span></td>
                      </>}

                      {activeTab === 'dgft' && <>
                        <td className="mono">{r.SHIPPING_BILL_NO}</td><td className="date-cell">{r.SB_DATE_FMT || '—'}</td>
                        <td className="mono">{r.IEC_CODE}</td>
                        <td><span className="badge-no">{r.ERROR_CODE || '—'}</span></td>
                        <td>{r.CUSTOMS_FILE_NAME || '—'}</td>
                      </>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="pagination">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="page-btn">← Prev</button>
              <span className="page-info">Page {page} / {Math.ceil(total / limit) || 1}</span>
              <button disabled={page >= Math.ceil(total/limit)} onClick={() => setPage(p => p + 1)} className="page-btn">Next →</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}